import { useState, useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useUser, Show } from "@clerk/react";
import { AuthModal } from "@/components/ui/auth-modal";
import { useGenerate } from "@/hooks/use-generation";
import { useUserStore } from "@/stores/user-store";
import { trackImageUploaded, trackCreditsModalShown } from "@/services/analytics";
import {
  T2V_MODEL,
  getModelOption,
  getGenerationCreditCost,
  INTEGRATED_MODEL_ID,
  AFFILIATE_DEFAULT_MODEL_ID,
  INTEGRATED_DURATION_MIN,
  type ModelId,
  type DurationOption,
} from "@/config/models";
import { UploadZone } from "./upload-zone";
import { ModelSelector } from "./model-selector";
import { Loader2, Sparkles, Hexagon } from "lucide-react";
import { NsfwOrError } from "@/components/ui/nsfw-error";
import { UpsellModal } from "@/components/ui/upsell-modal";
import { cn } from "@/lib/utils";

const DRAFT_KEY = "animepup_draft_animate";

function saveDraft(imageUrl: string | null, prompt: string) {
  try {
    localStorage.setItem(DRAFT_KEY, JSON.stringify({ imageUrl, prompt }));
  } catch {}
}

function loadDraft(): { imageUrl: string | null; prompt: string } | null {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && (parsed.imageUrl || parsed.prompt)) return parsed;
  } catch {}
  return null;
}

function clearDraft() {
  try { localStorage.removeItem(DRAFT_KEY); } catch {}
}

// Marks intent-to-generate gated behind signup; on the post-signup return we
// auto-fire the generation so the user never re-clicks Generate.
const AUTOGEN_KEY = "animepup_autogen_pending";
function setPendingAutogen() { try { localStorage.setItem(AUTOGEN_KEY, String(Date.now())); } catch {} }
function hasPendingAutogen() {
  try {
    const v = localStorage.getItem(AUTOGEN_KEY);
    const t = v ? Number(v) : 0;
    // Only honor a fresh flag (covers the signup redirect window). Ignore stale
    // flags from a dismissed modal so we never auto-fire an unwanted, credit-
    // spending generation on a later sign-in.
    return t > 0 && Date.now() - t < 15 * 60 * 1000;
  } catch { return false; }
}
function clearPendingAutogen() { try { localStorage.removeItem(AUTOGEN_KEY); } catch {} }

function getDeviceType(): "mobile" | "tablet" | "desktop" {
  const width = window.innerWidth;
  if (width < 768) return "mobile";
  if (width < 1024) return "tablet";
  return "desktop";
}

type ImageMeta = { width: number; height: number; megapixels: number };

// Images carried over from the landing page or restored from a draft never pass
// through the upload handler, so their dimensions are unknown at generate time —
// which is the majority of first generations. Measure on demand instead. Capped
// so analytics can never hold up a generation; the bitmap is normally already
// decoded because it is on screen.
function measureImage(url: string, timeoutMs = 500): Promise<ImageMeta | null> {
  return new Promise((resolve) => {
    let settled = false;
    const done = (meta: ImageMeta | null) => {
      if (settled) return;
      settled = true;
      resolve(meta);
    };
    const timer = setTimeout(() => done(null), timeoutMs);
    const img = new Image();
    img.onload = () => {
      clearTimeout(timer);
      const { naturalWidth: w, naturalHeight: h } = img;
      done(w && h ? { width: w, height: h, megapixels: (w * h) / 1e6 } : null);
    };
    img.onerror = () => {
      clearTimeout(timer);
      done(null);
    };
    img.src = url;
  });
}

export interface PromptPreset {
  label: string;
  prompt: string;
}

interface GenerateFormProps {
  initialImageUrl?: string | null;
  initialPrompt?: string;
  forceAuth?: boolean;
  affiliateFunnel?: boolean;
  presets?: PromptPreset[];
}

export function GenerateForm({ initialImageUrl, initialPrompt, forceAuth, affiliateFunnel, presets }: GenerateFormProps = {}) {
  const navigate = useNavigate();
  const { isSignedIn, isLoaded: isClerkLoaded, user: clerkUser } = useUser();
  const { credits, hasPurchased, isLoading: isUserLoading } = useUserStore();
  const generateMutation = useGenerate();
  // Free credits only cover Classic Standard 5s — lock every unpurchased user
  // (organic + affiliate) to that model until they buy a pack.
  const freeCreditsLocked = !hasPurchased;

  const hasProps = !!(initialImageUrl || initialPrompt);
  const draft = hasProps ? null : loadDraft();

  const [imageUrl, setImageUrl] = useState<string | null>(initialImageUrl ?? draft?.imageUrl ?? null);
  const [imageMeta, setImageMeta] = useState<ImageMeta | null>(null);
  // Whether the image was picked here or arrived with the user. Distinguishes the
  // landing-page funnel from in-app use, which the timing fields cannot do.
  const [imageSource, setImageSource] = useState<"upload" | "carried" | null>(
    initialImageUrl ?? draft?.imageUrl ? "carried" : null,
  );
  const [prompt, setPrompt] = useState(initialPrompt || draft?.prompt || "");
  const [promptEditCount, setPromptEditCount] = useState(0);
  const formMountedAt = useRef(Date.now());
  // Free → Classic Standard; purchased → Power (set after store loads).
  const [modelId, setModelId] = useState<ModelId>(AFFILIATE_DEFAULT_MODEL_ID);
  const [duration, setDuration] = useState<DurationOption>("5s");
  const [integratedSeconds, setIntegratedSeconds] = useState(INTEGRATED_DURATION_MIN);
  const [aspectRatio, setAspectRatio] = useState("16:9");
  const [showAuth, setShowAuth] = useState(false);
  const [authTitle, setAuthTitle] = useState("Sign Up to View Generation");
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [fakeLoading, setFakeLoading] = useState(false);
  const purchasedDefaultApplied = useRef(false);

  const openAuthModal = useCallback((title = "Sign Up to View Generation") => {
    setPendingAutogen();
    setAuthTitle(title);
    setShowAuth(true);
  }, []);

  useEffect(() => {
    if (isUserLoading) return;
    if (freeCreditsLocked) {
      setModelId(AFFILIATE_DEFAULT_MODEL_ID);
      setDuration("5s");
      purchasedDefaultApplied.current = false;
      return;
    }
    if (purchasedDefaultApplied.current) return;
    purchasedDefaultApplied.current = true;
    setModelId(INTEGRATED_MODEL_ID);
  }, [freeCreditsLocked, isUserLoading]);

  useEffect(() => {
    if (!isClerkLoaded) return;
    if (!forceAuth || isSignedIn) return;
    setPendingAutogen();
    setAuthTitle("Sign Up to View Generation");
    setFakeLoading(true);
    const timer = setTimeout(() => {
      setFakeLoading(false);
      setShowAuth(true);
    }, 2000);
    return () => clearTimeout(timer);
  }, [isClerkLoaded, forceAuth, isSignedIn]);

  useEffect(() => {
    saveDraft(imageUrl, prompt);
  }, [imageUrl, prompt]);

  const isTextToVideo = !imageUrl;
  const effectiveModelId = !isTextToVideo && freeCreditsLocked ? AFFILIATE_DEFAULT_MODEL_ID : modelId;
  const selectedModel = isTextToVideo
    ? T2V_MODEL
    : (getModelOption(effectiveModelId) ?? getModelOption(AFFILIATE_DEFAULT_MODEL_ID)!);
  const canGenerate = prompt.trim().length > 0;
  const displayCost = getGenerationCreditCost(
    selectedModel,
    effectiveModelId === INTEGRATED_MODEL_ID ? integratedSeconds : undefined,
  );

  const handleImageChange = useCallback((url: string | null, meta?: { width: number; height: number }) => {
    setImageUrl(url);
    if (url && meta) {
      const megapixels = (meta.width * meta.height) / 1e6;
      setImageMeta({ width: meta.width, height: meta.height, megapixels });
      setImageSource("upload");
      trackImageUploaded({
        user_id: clerkUser?.id,
        image_width: meta.width,
        image_height: meta.height,
        image_megapixels: megapixels,
        device_type: getDeviceType(),
      });
    } else if (url) {
      // Measuring can fail, and keeping the previous image's dimensions here would
      // silently mis-describe the new one. Clear and let the generate-time
      // fallback re-measure from the URL.
      setImageMeta(null);
      setImageSource("upload");
    } else {
      setImageMeta(null);
      setImageSource(null);
    }
  }, [clerkUser?.id]);

  const handleGenerate = useCallback(async ({ autogen = false }: { autogen?: boolean } = {}) => {
    if (!prompt.trim() || isUserLoading) return;

    if (credits < displayCost) {
      trackCreditsModalShown({
        user_id: clerkUser?.id,
        source: "generate_insufficient_credits",
        has_purchased: hasPurchased,
      });
      setShowCreditsModal(true);
      return;
    }

    // An autogen run fires from an effect the moment the user lands signed-in, so
    // ms_on_form and prompt_edit_count are near-zero by construction on that path.
    // Send the flag so those fields can be read conditionally rather than being
    // mistaken for a deliberate, fast user.
    const meta = imageMeta ?? (!isTextToVideo && imageUrl ? await measureImage(imageUrl) : null);
    if (meta && !imageMeta) setImageMeta(meta);
    try {
      const params: Record<string, unknown> = {
        model_id: isTextToVideo ? T2V_MODEL.id : effectiveModelId,
        prompt: prompt.trim(),
        device_type: getDeviceType(),
        ms_on_form: Date.now() - formMountedAt.current,
        prompt_edit_count: promptEditCount,
        had_billing_pageview_before_gen:
          sessionStorage.getItem("visited_billing") === "1",
        is_autogen: autogen,
        image_source: isTextToVideo ? null : imageSource,
      };

      if (isTextToVideo) {
        params.aspect_ratio = aspectRatio;
      } else {
        params.reference_image = imageUrl;
        if (meta) {
          params.image_width = meta.width;
          params.image_height = meta.height;
          params.image_megapixels = meta.megapixels;
        }
      }

      if (!isTextToVideo && effectiveModelId === INTEGRATED_MODEL_ID) {
        params.duration_seconds = integratedSeconds;
      }

      const result = await generateMutation.mutateAsync(params as any);
      if (result.video?.id) {
        clearDraft();
        navigate(`/generation/${result.video.id}`);
      }
    } catch (err) {
      console.error("Generation failed:", err);
    }
  }, [imageUrl, prompt, effectiveModelId, integratedSeconds, aspectRatio, credits, displayCost, isTextToVideo, generateMutation, navigate, isUserLoading, imageMeta, imageSource, promptEditCount, clerkUser?.id, hasPurchased]);

  // Auto-fire the generation after a post-signup redirect so the user doesn't
  // re-click Generate. Once, only when authed, store loaded, and draft ready.
  const autoGenFiredRef = useRef(false);
  useEffect(() => {
    if (autoGenFiredRef.current) return;
    if (!isClerkLoaded || !isSignedIn || isUserLoading) return;
    if (!hasPendingAutogen() || !canGenerate) return;
    autoGenFiredRef.current = true;
    clearPendingAutogen();
    handleGenerate({ autogen: true });
  }, [isClerkLoaded, isSignedIn, isUserLoading, canGenerate, handleGenerate]);

  const renderGenerateButtons = () => (
    <>
      <Show when="signed-out">
        <div>
          <button
            onClick={() => {
              setPendingAutogen();
              setAuthTitle("Sign Up to View Generation");
              setFakeLoading(true);
              setTimeout(() => {
                setFakeLoading(false);
                setShowAuth(true);
              }, 2000);
            }}
            disabled={fakeLoading}
            className="btn-primary w-full py-3 text-[14px] flex items-center justify-center gap-2 rounded-xl"
          >
            {fakeLoading ? (
              <><Loader2 className="size-4 animate-spin" />Generating...</>
            ) : (
              <>
                <Sparkles className="size-4" />
                Generate Video
              </>
            )}
          </button>
          {!isTextToVideo && !affiliateFunnel && (
            <p className="flex items-center justify-center gap-1 text-[10px] text-muted-foreground/50 mt-1.5">
              <Hexagon className="size-2.5" />{displayCost} gizmos
            </p>
          )}
        </div>
      </Show>

      <Show when="signed-in">
        <div>
          {isUserLoading ? (
            <div className="w-full h-[46px] rounded-xl bg-[#d4a574]/20 animate-pulse" />
          ) : (
            <button
              onClick={() => handleGenerate()}
              disabled={!canGenerate || generateMutation.isPending}
              className="btn-primary w-full py-3 text-[14px] flex items-center justify-center gap-2 rounded-xl"
            >
              {generateMutation.isPending ? (
                <><Loader2 className="size-4 animate-spin" />Generating...</>
              ) : (
                <>
                  <Sparkles className="size-4" />
                  Generate Video
                </>
              )}
            </button>
          )}
          {!isTextToVideo && (
            <p className="flex items-center justify-center gap-1 text-[10px] text-muted-foreground/50 mt-1.5">
              <Hexagon className="size-2.5" />{displayCost} gizmos
            </p>
          )}
        </div>
      </Show>
    </>
  );

  return (
    <div className="space-y-4">
      <UploadZone imageUrl={imageUrl} onImageChange={handleImageChange} />

      <textarea
        value={prompt}
        onChange={(e) => {
          setPrompt(e.target.value);
          setPromptEditCount((c) => c + 1);
        }}
        placeholder={imageUrl
          ? "Character turns and smiles, hair flowing in the breeze, cherry blossoms falling around her..."
          : "A cozy anime café at sunset, soft light streaming through windows, a cat sleeping on the counter, lo-fi vibes..."
        }
        rows={3}
        className="input-field resize-none"
      />

      {presets && presets.length > 0 && !prompt.trim() && !imageUrl && (
        <div className="flex flex-wrap gap-2">
          {presets.map((preset) => (
            <button
              key={preset.label}
              type="button"
              onClick={() => setPrompt(preset.prompt)}
              className={cn(
                "text-[12px] font-semibold px-3 py-1.5 rounded-lg transition-colors border",
                prompt === preset.prompt
                  ? "card-glass text-[#d4a574] border-[#d4a574]/20"
                  : "text-muted-foreground bg-white/5 border-white/8 hover:bg-white/10 hover:text-foreground",
              )}
            >
              {preset.label}
            </button>
          ))}
        </div>
      )}

      {imageUrl ? (
        <ModelSelector
          value={modelId}
          duration={duration}
          onChange={setModelId}
          onDurationChange={setDuration}
          integratedSeconds={integratedSeconds}
          onIntegratedSecondsChange={setIntegratedSeconds}
          lockToAffiliateDefault={freeCreditsLocked}
          onRequireAuth={
            isSignedIn ? undefined : () => openAuthModal("Sign Up to Unlock")
          }
        />
      ) : null}

      <div className="hidden md:block">
        {renderGenerateButtons()}
      </div>

      <div className="h-20 md:hidden" aria-hidden="true" />

      {createPortal(
        <div className="md:hidden fixed bottom-0 inset-x-0 z-50">
          <div className="px-5 pt-3 pb-3 bg-[#1a1520]/95 backdrop-blur-xl border-t border-white/6">
            {renderGenerateButtons()}
          </div>
          <div className="bg-[#1a1520]/95 backdrop-blur-xl" style={{ height: 'env(safe-area-inset-bottom, 0px)' }} />
        </div>,
        document.body,
      )}

      <AuthModal
        isOpen={showAuth}
        onClose={() => setShowAuth(false)}
        title={authTitle}
        previewImageUrl={authTitle === "Sign Up to Unlock" ? null : imageUrl}
      />

      {generateMutation.isError && <NsfwOrError message={generateMutation.error?.message} />}

      <UpsellModal
        isOpen={showCreditsModal}
        onClose={() => setShowCreditsModal(false)}
        creditCost={displayCost}
        credits={credits}
      />
    </div>
  );
}
